import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Diamond, Send, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";

export default function ResellerDashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const d = await api.get("/reseller/dashboard");
      setData(d.data);
      const r = await api.get("/reseller/recharges");
      setRecent(r.data.slice(0, 8));
    })();
  }, []);

  const u = data?.user;
  return (
    <div className="space-y-8" data-testid="reseller-dashboard">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Reseller Console</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mt-1">
          Welcome back{u?.name ? `, ${u.name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-zinc-500 mt-2 max-w-2xl">Allocate, recharge, audit. Your diamond balance is your hard cap.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="md:col-span-2 bg-zinc-950 text-white rounded-lg p-6" data-testid="balance-widget">
          <div className="flex items-start justify-between mb-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Diamond Balance by Platform</div>
            <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
              <Diamond className="w-4 h-4" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(u?.balances || {}).map(([key, b]) => (
              <div key={key} className="rounded-lg bg-white/5 p-4" data-testid={`balance-${key}`}>
                <div className="text-xs uppercase tracking-wider text-zinc-400">{b.name || key}</div>
                <div className="font-display text-3xl font-semibold tabular-nums font-mono mt-1">
                  {(b.balance ?? 0).toLocaleString()}
                </div>
                <div className="mt-2 text-[11px] text-zinc-400 font-mono">
                  {(b.allocated ?? 0).toLocaleString()} allocated · {(b.used ?? 0).toLocaleString()} used
                </div>
              </div>
            ))}
          </div>
        </div>

        <StatCard label="Recharges total" value={data?.stats?.total_recharges ?? "—"} icon={History} testId="stat-total" />
        <StatCard label="Today" value={data?.stats?.today_recharges ?? "—"} icon={Send} testId="stat-today" />
      </div>

      <div className="flex gap-3">
        <Link to="/reseller/recharge">
          <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="new-recharge-cta">
            <Send className="w-4 h-4 mr-2" /> New Recharge
          </Button>
        </Link>
        <Link to="/reseller/history">
          <Button variant="outline" data-testid="history-cta">
            <History className="w-4 h-4 mr-2" /> View History
          </Button>
        </Link>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg">
        <div className="p-6 border-b border-zinc-100">
          <h2 className="font-display text-lg font-medium text-zinc-900">Recent activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Time</th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Recipient UID</th>
                <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Diamonds</th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan="4" className="px-6 py-12 text-center text-zinc-400 text-sm">No recharges yet — start with &quot;New Recharge&quot;.</td></tr>
              )}
              {recent.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-6 py-3 text-sm text-zinc-600 font-mono">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3 text-sm text-zinc-900 font-mono">{r.recipient_uid}</td>
                  <td className="px-6 py-3 text-sm text-right tabular-nums font-mono text-zinc-900">{r.diamonds}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                      r.status === "success" ? "bg-green-100 text-green-800" :
                      r.status === "failed" ? "bg-red-100 text-red-800" :
                      "bg-amber-100 text-amber-800"
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
